import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  TrendingUp, Video, Calendar, FolderOpen, CheckCircle2, Image
} from "lucide-react";
import { Card, CardContent } from "../ui/card";

/**
 * StatsGrid Component
 * Displays key metrics: campaigns, content, scheduled posts, etc.
 */
export default function StatsGrid({ stats, loading, selectedCampaignName }) {
  const statItems = [
    { 
      label: selectedCampaignName ? "Campaign Status" : "Active Campaigns", 
      value: selectedCampaignName ? (stats.status || "Active") : stats.active_campaigns, 
      icon: TrendingUp, 
      color: "text-green-500",
      bgColor: "bg-green-500/10",
      isText: !!selectedCampaignName
    },
    { 
      label: "Total Content", 
      value: stats.total_content, 
      icon: Video, 
      color: "text-blue-500",
      bgColor: "bg-blue-500/10"
    },
    { 
      label: "Scheduled", 
      value: stats.scheduled_posts, 
      icon: Calendar, 
      color: "text-yellow-500",
      bgColor: "bg-yellow-500/10"
    },
    { 
      label: "Published", 
      value: stats.published_posts, 
      icon: CheckCircle2, 
      color: "text-purple-500",
      bgColor: "bg-purple-500/10"
    }
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => (
          <Card key={i} className="glass-card border-white/5 animate-pulse">
            <CardContent className="p-4">
              <div className="h-16 bg-white/5 rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {selectedCampaignName && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="col-span-2 md:col-span-4"
        >
          <div className="text-sm text-primary mb-2">
            Viewing stats for: <span className="font-semibold">{selectedCampaignName}</span>
          </div>
        </motion.div>
      )}
      {statItems.map((stat, i) => (
        <motion.div
          key={stat.label}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
        >
          <Card className="glass-card border-white/5 hover:border-primary/20 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div>
                  <p className={`font-bold ${stat.isText ? 'text-lg capitalize' : 'text-2xl'}`}>{stat.value ?? 0}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  );
}
